import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { Input } from '../primitives/input';
import type { VariantProps } from '../tokens/variants';
import { cn } from '../tokens/cn';
import { focusRing } from '../tokens/variants';
import { inputVariants } from '../primitives/input.variants';

export interface PasswordInputProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'>,
    VariantProps<typeof inputVariants> {
  /** Accessible label for the show/hide toggle button. */
  showLabel?: string;
  hideLabel?: string;
}

const paddingBySize = {
  sm: 'pr-9',
  md: 'pr-10',
  lg: 'pr-12',
};

const buttonSizeBySize = {
  sm: { btn: 'h-6 w-6 right-1', icon: 14 },
  md: { btn: 'h-7 w-7 right-1.5', icon: 16 },
  lg: { btn: 'h-8 w-8 right-2', icon: 18 },
};

/**
 * Password input with an inline show/hide toggle button. Pair with FormField
 * for label, error and helper text.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      className,
      size = 'md',
      showLabel = 'Show password',
      hideLabel = 'Hide password',
      disabled,
      ...props
    },
    ref,
  ) => {
    const [visible, setVisible] = useState(false);
    const sz = size ?? 'md';
    const btn = buttonSizeBySize[sz];

    return (
      <div className="relative w-full">
        <Input
          ref={ref}
          type={visible ? 'text' : 'password'}
          size={sz}
          disabled={disabled}
          className={cn(paddingBySize[sz], className)}
          {...props}
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          className={cn(
            'absolute top-1/2 -translate-y-1/2',
            'inline-flex items-center justify-center rounded-tk-sm',
            'text-[var(--tk-surface-fg-muted)] hover:text-[var(--tk-surface-fg)]',
            'hover:bg-[var(--tk-surface-bg)]',
            'transition-colors duration-150',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            focusRing,
            btn.btn,
          )}
        >
          {visible ? (
            <IconEyeOff size={btn.icon} />
          ) : (
            <IconEye size={btn.icon} />
          )}
        </button>
      </div>
    );
  },
);

PasswordInput.displayName = 'PasswordInput';
